import React, {useEffect, useRef, useState} from "react";
import {db} from "../../firebase/FirebaseProvider";
import {
    getAuth,
    GoogleAuthProvider,
    onAuthStateChanged,
    sendSignInLinkToEmail,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut
} from "firebase/auth";
import {useNavigate} from "react-router";
import {collection, doc, addDoc, getDoc, getDocs, query, setDoc, where} from "@firebase/firestore";
// User type not needed in this file after refactor
import {Card} from "react-bootstrap";
import PageTracking from "./PageTracking";
import "./AuthEmail.css";

const GoogleLogo: React.FC = () => (
    <svg className="auth-google-logo" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M21.35 12.27c0-.79-.07-1.55-.24-2.27H12v4.3h5.22a4.46 4.46 0 0 1-1.94 2.93v2.42h3.13c1.83-1.69 2.94-4.18 2.94-7.38Z"/>
        <path fill="#34A853" d="M12 21.5c2.61 0 4.8-.86 6.41-2.33l-3.13-2.42c-.87.58-1.98.92-3.28.92-2.52 0-4.65-1.7-5.42-3.98H3.35v2.5A9.68 9.68 0 0 0 12 21.5Z"/>
        <path fill="#FBBC05" d="M6.58 13.69A5.82 5.82 0 0 1 6.28 12c0-.59.1-1.16.3-1.69v-2.5H3.35A9.5 9.5 0 0 0 2.5 12c0 1.53.37 2.97.85 4.19l3.23-2.5Z"/>
        <path fill="#EA4335" d="M12 6.33c1.42 0 2.7.49 3.7 1.45l2.77-2.77C16.8 3.46 14.61 2.5 12 2.5a9.68 9.68 0 0 0-8.65 5.31l3.23 2.5C7.35 8.03 9.48 6.33 12 6.33Z"/>
    </svg>
);

const AuthEmail: React.FC = () => {
    const [authState, setAuthState] = useState<number>(-1);
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [passwordStatus, setPasswordStatus] = useState<number>(-1);
    const [feedback, setFeedback] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [register, setRegister] = useState<boolean>(false);
    const [registering, setRegistering] = useState<boolean>(false);
    const [googleSigningIn, setGoogleSigningIn] = useState<boolean>(false);
    const registeringRef = useRef<boolean>(false);

    const navigate = useNavigate();

    const normalizeEmail = (emailToNormalize: string): string => {
        return emailToNormalize.trim().toLowerCase();
    };

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (!currentUser || !currentUser.email) {
                setAuthState(0);
                return;
            }

            const normalizedEmail = normalizeEmail(currentUser.email);
            const authsRef = doc(db, 'auths', currentUser.uid);
            const authsSnap = await getDoc(authsRef);
            if (authsSnap.exists() && authsSnap.data().userId) {
                const userId = authsSnap.data().userId;
                await setDoc(authsRef, {
                    id: currentUser.uid,
                    email: normalizedEmail
                }, {merge: true});
                await setDoc(doc(db, 'users', userId), {
                    authId: currentUser.uid,
                    id: userId
                }, {merge: true});
                setAuthState(2);
                console.log("User already exists, navigating to home");
                navigate("/home", {replace: true});
                return;
            }

            // User should only ever hit this once
            await setDoc(authsRef, {
                id: currentUser.uid,
                email: normalizedEmail
            }, {merge: true});

            const userCollection = query(collection(db, 'users'), where('email', '==', normalizedEmail));
            const userCollectionSnap = await getDocs(userCollection);
            if(userCollectionSnap.size > 1) {
                alert("An error has occurred, please contact support.");
                return;
            }
            userCollectionSnap.forEach((userDoc) => {
                setDoc(authsRef, {
                    userId: userDoc.id
                }, {merge: true}).then(() => {

                    const usersRef = doc(db, 'users', userDoc.id);
                    setDoc(usersRef, {
                        authId: currentUser.uid,
                        id: userDoc.id
                    }, {merge: true}).then(() => {
                        navigate('/home', {replace: true});
                    });

                });

            });

            setAuthState(0);
        });

        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async () => {
        if(!password) {
            await loginWithEmail();
        } else {
            await loginWithPassword();
        }
    };

    const loginWithEmail = async () => {

        // Check to see if user actually exists before letting them login
        const normalizedEmail = normalizeEmail(email);
        const userCollection = query(collection(db, 'users'), where('email', '==', normalizedEmail));
        const userCollectionSnap = await getDocs(userCollection);
        if(userCollectionSnap.size === 0) {
            alert("Sorry, you don't have an existing user profile, please register first");
            return;
        }

        const actionCodeSettings = {
            url: `${window.location.origin}/auth/response`,
            handleCodeInApp: true
        };
        const auth = getAuth();
        await sendSignInLinkToEmail(auth, normalizedEmail, actionCodeSettings);
        window.localStorage.setItem('emailForSignIn', normalizedEmail);
        setAuthState(1);
    };

    const registerUser = async (emailParam: string, nameParam: string) => {
        if (registeringRef.current) {
            return;
        }

        registeringRef.current = true;
        setRegistering(true);
        setFeedback('');
        setPasswordStatus(-1);

        try {
            setPassword('');
            await createUserIfDoesNotExist(emailParam, nameParam);
            await loginWithEmail();
        } catch (error: any) {
            console.error("Registration failed:", error);
            setFeedback(error.message || "Registration failed. Please try again.");
            setPasswordStatus(0);
            registeringRef.current = false;
            setRegistering(false);
        }
    };

    const createUserIfDoesNotExist = async (emailParam: string, nameParam: string) => {
        const normalizedEmail = normalizeEmail(emailParam);
        if (!normalizedEmail) {
            throw new Error("Please enter an email address.");
        }

        let finalName = nameParam;
        if(!finalName) {
            finalName = 'Anonymous';
        }

        const usersRef = collection(db, 'users');
        const userCollection = query(usersRef, where('email', '==', normalizedEmail));
        const userCollectionSnap = await getDocs(userCollection);

        if(userCollectionSnap.size > 1) {
            throw new Error("Multiple users already exist for this email address. Please contact support.");
        }

        if(userCollectionSnap.size === 1) {
            return;
        }

        const authsCollection = query(collection(db, 'auths'), where('email', '==', normalizedEmail));
        const authsCollectionSnap = await getDocs(authsCollection);
        if(authsCollectionSnap.size > 0) {
            throw new Error("A login already exists for this email address. Please log in instead.");
        }

        await addDoc(usersRef, {
            name: finalName,
            email: normalizedEmail,
            status: 'ACTIVE',
            enableUiV2: true,
        });
    };

    const loginWithPassword = async () => {

        // Check to see if user actually exists before letting them login
        const userCollection = query(collection(db, 'users'), where('email', '==', normalizeEmail(email)));
        const userCollectionSnap = await getDocs(userCollection);
        if(userCollectionSnap.size === 0) {
            alert("Sorry, you don't have an existing user profile, please register first");
            return;
        }

        setPasswordStatus(1);
        const auth = getAuth();
        signInWithEmailAndPassword(auth, normalizeEmail(email), password)
            .then((userCredential) => {
                setAuthState(1);
                navigate('/home', {replace: true});
            })
            .catch((error) => {
                if(error.message.includes('wrong-password')) {
                    setPasswordStatus(0);
                    setFeedback("Incorrect password, please try again.");
                } else {
                    setPasswordStatus(0);
                    setFeedback(error.message);
                }
            });
    };

    const signInWithGoogle = async () => {
        if (googleSigningIn) {
            return;
        }

        setFeedback('');
        setPasswordStatus(1);
        setGoogleSigningIn(true);

        try {
            const auth = getAuth();
            const result = await signInWithPopup(auth, new GoogleAuthProvider());
            const googleUser = result.user;
            const normalizedEmail = normalizeEmail(googleUser.email || '');

            if (!normalizedEmail) {
                throw new Error("Google did not provide an email address. Please use email login instead.");
            }

            const userCollection = query(collection(db, 'users'), where('email', '==', normalizedEmail));
            const userCollectionSnap = await getDocs(userCollection);

            if (userCollectionSnap.size > 1) {
                throw new Error("Multiple user profiles already exist for this email address. Please contact support.");
            }

            let userId: string;
            if (userCollectionSnap.size === 1) {
                userId = userCollectionSnap.docs[0].id;
            } else {
                if (!register) {
                    throw new Error("Sorry, you don't have an existing user profile, please register first.");
                }

                const userDoc = await addDoc(collection(db, 'users'), {
                    name: name.trim() || googleUser.displayName || 'Anonymous',
                    email: normalizedEmail,
                    status: 'ACTIVE',
                    enableUiV2: true,
                });
                userId = userDoc.id;
            }

            await setDoc(doc(db, 'auths', googleUser.uid), {
                id: googleUser.uid,
                email: normalizedEmail,
                userId
            }, {merge: true});
            await setDoc(doc(db, 'users', userId), {
                authId: googleUser.uid,
                id: userId
            }, {merge: true});

            // Let the app refresh its user state without a full page reload.
            window.dispatchEvent(new Event('stl-user-profile-ready'));
            navigate('/home', {replace: true});
        } catch (error: any) {
            console.error("Google sign-in failed:", error);

            if (error.code === 'auth/account-exists-with-different-credential') {
                setFeedback("This Google email already has a different login method. Please use the email login first; Google cannot be matched automatically until the providers are linked.");
            } else if (error.code === 'auth/popup-closed-by-user') {
                setFeedback('Google sign-in was cancelled.');
            } else {
                setFeedback(error.message || "Google sign-in failed. Please try again.");
            }

            setPasswordStatus(0);
            if (getAuth().currentUser) {
                await signOut(getAuth());
            }
        } finally {
            setGoogleSigningIn(false);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            login();
        }
    };

    return (
        <>
            <PageTracking />

            {authState === -1 &&
            <div className={'loading'}>
                Loading...
            </div>}

            {authState >= 0 &&
            <div className={""}>

                <Card className={"mb-3 auth-form-card"}>
                    <Card.Header>Welcome to 'Spoons Tracker!</Card.Header>
                    <Card.Body>
                        <div>Welcome, it's time to get ticking off those 'Spoons! Use this website to track your visits to Wetherspoon pubs.</div>
                        <div className={"mt-2"}>If you're new to the site please select register below and enter your name and email address.</div>
                    </Card.Body>
                </Card>

                {!register &&
                <Card className={"auth-form-card"}>
                    <Card.Header>Login</Card.Header>
                    <div className={"auth-form-content auth-login-content"}>
                        <div className="input-group mb-2">
                            <div className="input-group-prepend">
                                <span className="input-group-text" id="inputGroup-sizing-default">Email</span>
                            </div>
                            <input type="email"
                                   name={"email"}
                                   placeholder={"Email address"}
                                   onChange={e => setEmail(e.target.value)}
                                   className="form-control"
                                   aria-label="Default" aria-describedby="inputGroup-sizing-default" />
                        </div>
                        <div className="input-group mb-2">
                            <div className="input-group-prepend">
                                <span className="input-group-text" id="inputGroup-sizing-default">Password</span>
                            </div>
                            <input type="password"
                                   name={"password"}
                                   placeholder={"***********"}
                                   onKeyDown={(e) => {handleKeyDown(e)}}
                                   onChange={e => setPassword(e.target.value)}
                                   className="form-control"
                                   aria-label="Default" aria-describedby="inputGroup-sizing-default" />
                        </div>
                    {authState === 0 && passwordStatus === 0 && <div>
                            <div className={"alert alert-danger p-2"}>
                                <span>{feedback}</span>
                            </div>
                        </div>}
                        {(authState === 1 && passwordStatus !== 1) && <div className={"alert alert-success p-2 mb-0"}>Thanks, you'll now be sent an email containing a link. Please click on it to login to 'Spoons Tracker.</div>}
                    </div>
                    {(authState === 0 && passwordStatus === -1) &&
                    <div className={"auth-form-content"}>
                        <div className={"alert alert-info p-2 mb-0"}>No password? No problem. Just enter your email address and press login and you will receive a login link via email.</div>
                    </div>}
                    <div className={"p-3 auth-form-actions-wrap"}>
                        <div className={"auth-form-actions"}>
                            <button disabled={authState === 1 || googleSigningIn} className={"btn btn-primary mr-1"} onClick={() => {login()}}>Login</button>
                            <button disabled={authState === 1 || googleSigningIn} className={"btn btn-dark"} onClick={() => {setRegister(true)}}>Register</button>
                        </div>
                        <div className="auth-google-divider"><span>or</span></div>
                        <button disabled={authState === 1 || googleSigningIn} className="auth-google-button" onClick={signInWithGoogle}>
                            <GoogleLogo />
                            <span>{googleSigningIn ? "Connecting..." : "Continue with Google"}</span>
                        </button>
                    </div>
                </Card>}

                {register &&
                <Card className={"auth-form-card"}>
                    <Card.Header>Register</Card.Header>
                    <div className={"auth-form-content auth-register-content"}>
                        <div className={"mb-2"}>
                            <div className="input-group mb-2">
                                <div className="input-group-prepend">
                                    <span className="input-group-text" id="inputGroup-sizing-default">Email</span>
                                </div>
                                <input type="text"
                                       name={"email"}
                                       placeholder={"Email address"}
                                       onChange={e => setEmail(e.target.value)}
                                       className="form-control"
                                       aria-label="Default" aria-describedby="inputGroup-sizing-default" />
                            </div>
                        </div>
                        <div className={""}>
                            <div className="input-group mb-3">
                                <div className="input-group-prepend">
                                    <span className="input-group-text" id="inputGroup-sizing-default">Name</span>
                                </div>
                                <input type="text"
                                       name={"name"}
                                       placeholder={"Name or alias"}
                                       onChange={e => setName(e.target.value)}
                                       className="form-control"
                                       aria-label="Default" aria-describedby="inputGroup-sizing-default" />
                            </div>
                        </div>
                        {(authState === 0 && passwordStatus === 0 && feedback) && <div className={"alert alert-danger p-2"}>
                            <span>{feedback}</span>
                        </div>}
                        {(authState === 1 && passwordStatus !== 1) && <div className={"alert alert-success p-2 mb-0"}>Thanks, you'll now be sent an email containing a link. Please click on it to login to 'Spoons Tracker.</div>}
                    </div>
                    <div className={"p-3 auth-form-actions-wrap"}>
                        <div className={"auth-form-actions"}>
                            <button disabled={authState === 1 || registering || googleSigningIn} className={"btn btn-dark mr-1"} onClick={() => {setRegister(false)}}>Login</button>
                            <button disabled={authState === 1 || registering || googleSigningIn} className={"btn btn-primary"} onClick={() => {registerUser(email, name)}}>{registering ? "Registering..." : "Register"}</button>
                        </div>
                        <div className="auth-google-divider"><span>or</span></div>
                        <button disabled={authState === 1 || registering || googleSigningIn} className="auth-google-button" onClick={signInWithGoogle}>
                            <GoogleLogo />
                            <span>{googleSigningIn ? "Connecting..." : "Continue with Google"}</span>
                        </button>
                    </div>
                </Card>}

            </div>}
        </>
    );
};

export default AuthEmail;
